import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, Typography, withStyles, Grid, Tabs, Tab, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, TextField, Button } from '@material-ui/core';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { debounce } from 'lodash';
import ClickableGoalsProgressChart from './ClickableGoalsProgressChart'; 

const styles = {
  container: {
    margin: '20px',
  },
  card: {
    marginTop: '20px',
  },
  subtitle: {
    fontSize: '2.0rem', // Smaller font for subtitle
    color: '#A9A9A9', // Change color to #A9A9A9
  },
  activeUsers: {
    fontSize: '4.0rem', // Larger font for active users count
  },
  changeText: {
    fontSize: '2.0rem', // Smaller font for change text
  },
  metricCard: {
    marginTop: '20px',
  },
};

const formatNumber = (num) => {
  if (num === undefined || num === null) {
    return "0"; // or any default value you prefer
  }
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const renderCustomLabel = ({ value, x, y, width }) => {
  return (
    <text 
      fill="white" 
      fontSize={14} 
      textAnchor="middle" 
      x={x + width / 2}
      y={y + 15}
    >
      {value}
    </text>
  );
};

const SuccessMetricsContainer = ({ classes }) => {
  const [dateRange, setDateRange] = useState('today'); // Set default to 'today'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeUsers, setActiveUsers] = useState(0);
  const [previousActiveUsers, setPreviousActiveUsers] = useState(0);
  const [activeVisits, setActiveVisits] = useState(0);
  const [activeUsersSuccession, setActiveUsersSuccession] = useState(0);
  const [activeUsersGrowth, setActiveUsersGrowth] = useState(0);
  const [activeUsersNoMotivation, setActiveUsersNoMotivation] = useState(0);
  const [signUps, setSignUps] = useState(0);
  const [previousSignUps, setPreviousSignUps] = useState(0);
  const [successionCount, setSuccessionCount] = useState(0);
  const [previousSuccessionCount, setPreviousSuccessionCount] = useState(0);
  const [growthCount, setGrowthCount] = useState(0);
  const [previousGrowthCount, setPreviousGrowthCount] = useState(0);
  const [noMotivationCount, setNoMotivationCount] = useState(0);
  const [previousNoMotivationCount, setPreviousNoMotivationCount] = useState(0);
  const [valuationToolUsage, setValuationToolUsage] = useState({ complete: 0, incomplete: 0 });
  const [previousUsageCount, setPreviousUsageCount] = useState(0);
  const [profileViews, setProfileViews] = useState(0);
  const [previousProfileViews, setPreviousProfileViews] = useState(0);
  const [searches, setSearches] = useState(0);
  const [previousSearches, setPreviousSearches] = useState(0);
  const [newConversations, setNewConversations] = useState(0);
  const [newMessages, setNewMessages] = useState(0);
  const [previousNewMessages, setPreviousNewMessages] = useState(0);
  const [previousNewConversations, setPreviousNewConversations] = useState(0);
  const [accessedEducation, setAccessedEducation] = useState(0);
  const [previousAccessedEducation, setPreviousAccessedEducation] = useState(0);
  const [comparisonStartDate, setComparisonStartDate] = useState('');
  const [comparisonEndDate, setComparisonEndDate] = useState('');
  const [continuityBuilderCount, setContinuityBuilderCount] = useState(0);
  const [previousContinuityBuilderCount, setPreviousContinuityBuilderCount] = useState(0);
  const [dealsCreatedCount, setDealsCreatedCount] = useState(0);
  const [previousDealsCreatedCount, setPreviousDealsCreatedCount] = useState(0);
  const [matchesCount, setMatchesCount] = useState(0);
  const [previousMatchesCount, setPreviousMatchesCount] = useState(0);
  const [invitesCount, setInvitesCount] = useState(0);
  const [previousInvitesCount, setPreviousInvitesCount] = useState(0);
  const [proposalsCount, setProposalsCount] = useState(0);
  const [previousProposalsCount, setPreviousProposalsCount] = useState(0);
  const [buyerNdaSentCount, setBuyerNdaSentCount] = useState(0);
  const [previousBuyerNdaSentCount, setPreviousBuyerNdaSentCount] = useState(0);
  const [sellerNdaSentCount, setSellerNdaSentCount] = useState(0);
  const [previousSellerNdaSentCount, setPreviousSellerNdaSentCount] = useState(0);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [tabValue, setTabValue] = useState(0); // State to manage the active tab
  const [totalAdvisors, setTotalAdvisors] = useState(0);
  const [profileCompletionData, setProfileCompletionData] = useState({ complete: 0, incomplete: 0 });
  const [searchUsage, setSearchUsage] = useState({ complete: 0, incomplete: 0 });
  const [dealRoomUsage, setDealRoomUsage] = useState({ complete: 0, incomplete: 0 });
  const [continuityUsage, setContinuityUsage] = useState({ complete: 0, incomplete: 0 });
  const [messageUsage, setMessageUsage] = useState({ complete: 0, incomplete: 0 });
  const [milestones, setMilestones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Set default date range to today
    const today = new Date();
    const start = new Date(today.setHours(0, 0, 0, 0));
    const end = new Date(today.setHours(23, 59, 59, 999));

    // Set comparison dates for today
    const comparisonStart = new Date(today.setDate(today.getDate() - 1)).setHours(0, 0, 0, 0);
    const comparisonEnd = new Date(today.setDate(today.getDate() - 1)).setHours(23, 59, 59, 999);

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setComparisonStartDate(new Date(comparisonStart).toISOString().split('T')[0]);
    setComparisonEndDate(new Date(comparisonEnd).toISOString().split('T')[0]);
  }, []);

  const handleDateRangeChange = (event) => {
    const selectedRange = event.target.value;
    setDateRange(selectedRange);
    setStartDate('');
    setEndDate('');

    const today = new Date();
    let start, end, comparisonStart, comparisonEnd;

    switch (selectedRange) {
      case 'all_time':
        start = new Date('2015-01-01');
        end = new Date(today.setHours(23, 59, 59, 999));
        comparisonStart = new Date('2014-12-31'); // Previous day for comparison
        comparisonEnd = new Date('2014-12-31').setHours(23, 59, 59, 999);
        break;
      case 'today':
        start = new Date(today.setHours(0, 0, 0, 0));
        end = new Date(today.setHours(23, 59, 59, 999));
        comparisonStart = new Date(today.setDate(today.getDate() - 1)).setHours(0, 0, 0, 0);
        comparisonEnd = new Date(today.setDate(today.getDate() - 1)).setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        start = new Date(today.setDate(today.getDate() - 1));
        start.setHours(0, 0, 0, 0);
        end = new Date(today.setHours(23, 59, 59, 999));
        comparisonStart = new Date(today.setDate(today.getDate() - 2)).setHours(0, 0, 0, 0);
        comparisonEnd = new Date(today.setDate(today.getDate() - 2)).setHours(23, 59, 59, 999);
        break;
      case 'this_week':
        const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
        start = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
        end = new Date(firstDayOfWeek.setDate(firstDayOfWeek.getDate() + 6));
        end.setHours(23, 59, 59, 999);
        comparisonStart = new Date(firstDayOfWeek.setDate(firstDayOfWeek.getDate() - 7)).setHours(0, 0, 0, 0);
        comparisonEnd = new Date(firstDayOfWeek.setDate(firstDayOfWeek.getDate() - 7)).setHours(23, 59, 59, 999);
        break;
      case 'last_week':
        const lastWeekStart = new Date(today.setDate(today.getDate() - today.getDay() - 7));
        start = new Date(lastWeekStart.setHours(0, 0, 0, 0));
        end = new Date(lastWeekStart.setDate(lastWeekStart.getDate() + 6));
        end.setHours(23, 59, 59, 999);
        comparisonStart = new Date(lastWeekStart.setDate(lastWeekStart.getDate() - 7)).setHours(0, 0, 0, 0);
        comparisonEnd = new Date(lastWeekStart.setDate(lastWeekStart.getDate() - 7)).setHours(23, 59, 59, 999);
        break;
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        comparisonStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        comparisonEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        comparisonEnd.setHours(23, 59, 59, 999);
        break;
      case 'last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        comparisonStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        comparisonEnd = new Date(today.getFullYear(), today.getMonth() - 1, 0);
        comparisonEnd.setHours(23, 59, 59, 999);
        break;
      case 'this_quarter':
        const currentQuarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), currentQuarter * 3, 1);
        end = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);
        end.setHours(23, 59, 59, 999);
        comparisonStart = new Date(today.getFullYear(), currentQuarter * 3 - 3, 1);
        comparisonEnd = new Date(today.getFullYear(), currentQuarter * 3, 0);
        comparisonEnd.setHours(23, 59, 59, 999);
        break;
      case 'last_quarter':
        const lastQuarter = Math.floor((today.getMonth() - 3) / 3);
        start = new Date(today.getFullYear(), lastQuarter * 3, 1);
        end = new Date(today.getFullYear(), (lastQuarter + 1) * 3, 0);
        end.setHours(23, 59, 59, 999);
        comparisonStart = new Date(today.getFullYear(), lastQuarter * 3 - 3, 1);
        comparisonEnd = new Date(today.getFullYear(), lastQuarter * 3, 0);
        comparisonEnd.setHours(23, 59, 59, 999);
        break;
      case 'this_year':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        end.setHours(23, 59, 59, 999);
        comparisonStart = new Date(today.getFullYear() - 1, 0, 1);
        comparisonEnd = new Date(today.getFullYear() - 1, 11, 31);
        comparisonEnd.setHours(23, 59, 59, 999);
        break;
      case 'last_year':
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        end.setHours(23, 59, 59, 999);
        comparisonStart = new Date(today.getFullYear() - 2, 0, 1);
        comparisonEnd = new Date(today.getFullYear() - 2, 11, 31);
        comparisonEnd.setHours(23, 59, 59, 999);
        break;
      case 'custom':
        // Do nothing, let the user select custom dates
        break;
      default:
        break;
    }

    if (start && end) {
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
      setComparisonStartDate(new Date(comparisonStart).toISOString().split('T')[0]);
      setComparisonEndDate(new Date(comparisonEnd).toISOString().split('T')[0]);
    }
  };

  const fetchWithDelay = async (apiCalls) => {
    setIsLoading(true);
    // Process API calls with a small delay between each
    for (const call of apiCalls) {
      try {
        await call();
        // Add a 300ms delay between API calls
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (error) {
        console.error("Error in API call:", error);
      }
    }
    setIsLoading(false);
  };

  const fetchSignUps = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/sign_ups', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setSignUps(response.data.sign_ups);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/sign_ups', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousSignUps(previousResponse.data.sign_ups);
      } catch (error) {
        console.error("Error fetching sign-ups:", error);
      }
    }
  };

  const fetchMotivations = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/motivations', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });

        // Assuming the response contains counts for each motivation type
        setSuccessionCount(response.data.succession);
        setGrowthCount(response.data.growth);
        setNoMotivationCount(response.data.no_motivation);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/motivations', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });

        // Set previous counts
        setPreviousSuccessionCount(previousResponse.data.succession);
        setPreviousGrowthCount(previousResponse.data.growth);
        setPreviousNoMotivationCount(previousResponse.data.no_motivation);
      } catch (error) {
        console.error("Error fetching motivations:", error);
      }
    }
  };

  const fetchActiveUsers = async () => {
    if (startDate && endDate) {
      const response = await axios.get('/api/active_users', {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });
      setActiveUsers(response.data.active_users);

      // Calculate previous period dates
      const previousStartDate = new Date(startDate);
      const previousEndDate = new Date(endDate);
      previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
      previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

      const previousResponse = await axios.get('/api/active_users', {
        params: {
          start_date: previousStartDate.toISOString().split('T')[0],
          end_date: previousEndDate.toISOString().split('T')[0],
        },
      });
      setPreviousActiveUsers(previousResponse.data.active_users);

      // Fetch sign-ups
      await fetchSignUps();

      // Fetch motivations
      await fetchMotivations();

      // Fetch profile views
      await fetchProfileViews();

      // Fetch searches
      await fetchSearches();

      // Fetch new conversations
      await fetchNewConversations();

      // Fetch accessed education
      await fetchAccessedEducation();
    }
  };

  const fetchActiveVisitors = async () => {
    if (startDate && endDate) {
      const response = await axios.get('/api/active_visitors', {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });
      setActiveVisits(response.data.active_visitors);

      // Calculate previous period dates
      const previousStartDate = new Date(startDate);
      const previousEndDate = new Date(endDate);
      previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
      previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

      const previousResponse = await axios.get('/api/active_visitors', {
        params: {
          start_date: previousStartDate.toISOString().split('T')[0],
          end_date: previousEndDate.toISOString().split('T')[0],
        },
      });
      setPreviousActiveUsers(previousResponse.data.active_visitors);
    }
  };

  const fetchActiveUsersSuccession = async () => {
    if (startDate && endDate) {
      const response = await axios.get('/api/active_users_succession', {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });
      setActiveUsersSuccession(response.data.active_users_succession);
      // Calculate previous period dates
      const previousStartDate = new Date(startDate);
      const previousEndDate = new Date(endDate);
      previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
      previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));
      const previousResponse = await axios.get('/api/active_users_succession', {
        params: {
          start_date: previousStartDate.toISOString().split('T')[0],
          end_date: previousEndDate.toISOString().split('T')[0],
        },
      });
      setPreviousActiveUsers(previousResponse.data.active_users_succession);
    }
  };

  const fetchActiveUsersGrowth = async () => {
    if (startDate && endDate) {
      const response = await axios.get('/api/active_users_growth', {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });
      setActiveUsersGrowth(response.data.active_users_growth);

      // Calculate previous period dates
      const previousStartDate = new Date(startDate);
      const previousEndDate = new Date(endDate);
      previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
      previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

      const previousResponse = await axios.get('/api/active_users_growth', {
        params: {
          start_date: previousStartDate.toISOString().split('T')[0],
          end_date: previousEndDate.toISOString().split('T')[0],
        },
      });
      setPreviousActiveUsers(previousResponse.data.active_users_growth);
    }
  };

  const fetchActiveUsersNoMotivation = async () => {
    if (startDate && endDate) {
      const response = await axios.get('/api/active_users_no_motivation', {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });
      setActiveUsersNoMotivation(response.data.active_users_no_motivation);

      // Calculate previous period dates
      const previousStartDate = new Date(startDate);
      const previousEndDate = new Date(endDate);
      previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
      previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

      const previousResponse = await axios.get('/api/active_users_no_motivation', {
        params: {
          start_date: previousStartDate.toISOString().split('T')[0],
          end_date: previousEndDate.toISOString().split('T')[0],
        },
      });
      setPreviousActiveUsers(previousResponse.data.active_users_no_motivation);
    }
  };

  const fetchProfileViews = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/profile_views', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setProfileViews(response.data.profile_views);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/profile_views', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousProfileViews(previousResponse.data.profile_views);
      } catch (error) {
        console.error("Error fetching profile views:", error);
      }
    }
  };

  const fetchSearches = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/searches', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        console.log("Searches response:", response.data);
        setSearches(response.data.searches);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/searches', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousSearches(previousResponse.data.searches);
      } catch (error) {
        console.error("Error fetching searches:", error);
      }
    }
  };

  const fetchNewMessages = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/new_messages', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setNewMessages(response.data.new_messages);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/new_messages', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousNewMessages(previousResponse.data.new_messages);
      } catch (error) {
        console.error("Error fetching new messages:", error);
      }
    }
  };


  const fetchNewConversations = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/new_conversations', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setNewConversations(response.data.new_conversations);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/new_conversations', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousNewConversations(previousResponse.data.new_conversations);
      } catch (error) {
        console.error("Error fetching new conversations:", error);
      }
    }
  };

  const fetchAccessedEducation = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/accessed_education', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setAccessedEducation(response.data.accessed_education);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/accessed_education', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousAccessedEducation(previousResponse.data.accessed_education);
      } catch (error) {
        console.error("Error fetching accessed education:", error);
      }
    }
  };

  const fetchContinuityBuilder = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/continuity_builder', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setContinuityBuilderCount(response.data.continuity_builder);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/continuity_builder', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousContinuityBuilderCount(previousResponse.data.continuity_builder);
      } catch (error) {
        console.error("Error fetching continuity builder:", error);
      }
    }
  };

  const fetchDealsCreated = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/deals_created', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setDealsCreatedCount(response.data.deals_created);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/deals_created', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousDealsCreatedCount(previousResponse.data.deals_created);
      } catch (error) {
        console.error("Error fetching deals created:", error);
      }
    }
  };

  const fetchMatches = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/matches', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setMatchesCount(response.data.matches);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/matches', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousMatchesCount(previousResponse.data.matches);
      } catch (error) {
        console.error("Error fetching matches:", error);
      }
    }
  };

  const fetchInvites = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/invites', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setInvitesCount(response.data.invites);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/invites', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousInvitesCount(previousResponse.data.invites);
      } catch (error) {
        console.error("Error fetching invites:", error);
      }
    }
  };

  const fetchProposals = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/proposals', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setProposalsCount(response.data.proposals);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/proposals', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousProposalsCount(previousResponse.data.proposals);
      } catch (error) {
        console.error("Error fetching proposals:", error);
      }
    }
  };

  const fetchBuyerNdaSent = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/buyer_nda_sent', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setBuyerNdaSentCount(response.data.buyer_nda_sent);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/buyer_nda_sent', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousBuyerNdaSentCount(previousResponse.data.buyer_nda_sent);
      } catch (error) {
        console.error("Error fetching buyer NDA sent:", error);
      }
    }
  };

  const fetchSellerNdaSent = async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get('/api/seller_nda_sent', {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        setSellerNdaSentCount(response.data.seller_nda_sent);

        // Calculate previous period dates
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        previousStartDate.setMonth(previousStartDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1)); // Adjust for multiple months
        previousEndDate.setMonth(previousEndDate.getMonth() - (previousEndDate.getMonth() - previousStartDate.getMonth() + 1));

        const previousResponse = await axios.get('/api/seller_nda_sent', {
          params: {
            start_date: previousStartDate.toISOString().split('T')[0],
            end_date: previousEndDate.toISOString().split('T')[0],
          },
        });
        setPreviousSellerNdaSentCount(previousResponse.data.seller_nda_sent);
      } catch (error) {
        console.error("Error fetching seller NDA sent:", error);
      }
    }
  };

  const fetchPipelineStages = async () => {
    try {
      const response = await axios.get('/api/deal_tracking_pipeline', {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });
      setPipelineStages(response.data.pipeline_stages);
    } catch (error) {
      console.error("Error fetching pipeline stages:", error);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      if (startDate && endDate) {
        setIsLoading(true);
        
        try {
          await fetchActiveUsers();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchActiveVisitors();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchActiveUsersSuccession();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchActiveUsersGrowth();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchActiveUsersNoMotivation();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchNewMessages();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchContinuityBuilder();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchDealsCreated();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchMatches();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchInvites();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchProposals();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchBuyerNdaSent();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchSellerNdaSent();
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          await fetchPipelineStages();
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    if (startDate && endDate && dateRange !== 'custom') {
      const debouncedFetch = debounce(fetchAllData, 500);
      debouncedFetch();
      
      return () => {
        debouncedFetch.cancel();
      };
    }
  }, [startDate, endDate, dateRange]);

  useEffect(() => {
    const fetchProfileCompletionData = async () => {
      try {
        const response = await axios.get('/api/advisors/profile_completion'); // Adjust the API endpoint as needed
        console.debug("Response from profile completion API:", response.data); // Debug log
        const complete = response.data.complete; // Advisors with profile_percentage_complete: 100
        const partial = response.data.partial; // Advisors with profile_percentage_complete >= 60
        const incomplete = response.data.incomplete; // Advisors with profile_percentage_complete < 60
        const complete_count = response.data.complete_count; // Advisors with profile_percentage_complete: 100
        const partial_count = response.data.partial_count; // Advisors with profile_percentage_complete >= 60
        const incomplete_count = response.data.incomplete_count; // Advisors with profile_percentage_complete < 60
        const goal = response.data.goal;
        const average_time = response.data.average_time;

        setProfileCompletionData({ complete, incomplete, complete_count, incomplete_count, partial, partial_count, goal, average_time });
      } catch (error) {
        console.error("Error fetching profile completion data:", error);
      }
    };

    fetchProfileCompletionData();
  }, []);

  useEffect(() => {
    console.log("useEffect triggered"); // Debug log
    const fetchValuationData = async () => {
      console.log("Fetching valuation data..."); // Debug log
      try {
        const response = await axios.get('/api/valuation_completion'); // Adjust the API endpoint as needed
        console.log("Response from valuation API:", response.data); // Debug log
        const complete = response.data.complete; // Users who have accessed the valuation tool
        const incomplete = response.data.incomplete; // Users who have not accessed the valuation tool
        const complete_count = response.data.complete_count; // Users who have completed searches
        const incomplete_count = response.data.incomplete_count; // Users who have not completed searches
        const goal = response.data.goal;
        const average_time = response.data.average_time;

        setValuationToolUsage({ complete, incomplete, complete_count, incomplete_count, goal, average_time });
      } catch (error) {
        console.error("Error fetching valuation tool usage:", error);
      }
    };

    fetchValuationData();
  }, []);

  useEffect(() => {
    const fetchDealRoomUsage = async () => {
      try {
        const response = await axios.get('/api/succession_deal_room_completion'); // Adjust the API endpoint as needed
        const complete = response.data.complete; // Users who have completed searches
        const incomplete = response.data.incomplete; // Users who have not completed searches
        const complete_count = response.data.complete_count; // Users who have completed searches
        const incomplete_count = response.data.incomplete_count; // Users who have not completed searches
        const goal = response.data.goal;
        const average_time = response.data.average_time;

        setDealRoomUsage({ complete, incomplete, complete_count, incomplete_count, goal, average_time });
      } catch (error) {
        console.error("Error fetching deal room usage:", error);
      }
    };

    fetchDealRoomUsage();
  }, []);

  useEffect(() => {
    const fetchSearchUsage = async () => {
      try {
        const response = await axios.get('/api/search_completion'); // Adjust the API endpoint as needed
        const complete = response.data.complete; // Users who have completed searches
        const incomplete = response.data.incomplete; // Users who have not completed searches
        const complete_count = response.data.complete_count; // Users who have completed searches
        const incomplete_count = response.data.incomplete_count; // Users who have not completed searches
        const goal = response.data.goal;
        const average_time = response.data.average_time;

        setSearchUsage({ complete, incomplete, complete_count, incomplete_count, goal, average_time });
      } catch (error) {
        console.error("Error fetching search usage:", error);
      }
    };

    fetchSearchUsage();
  }, []);

  useEffect(() => {
    const fetchContinuityUsage = async () => {
      try {
        const response = await axios.get('/api/continuity_builder_completion'); // Adjust the API endpoint as needed
        const complete = response.data.complete; // Users who have completed searches
        const incomplete = response.data.incomplete; // Users who have not completed searches
        const complete_count = response.data.complete_count; // Users who have completed searches
        const incomplete_count = response.data.incomplete_count; // Users who have not completed searches
        const goal = response.data.goal;
        const average_time = response.data.average_time;

        setContinuityUsage({ complete, incomplete, complete_count, incomplete_count, goal, average_time });
      } catch (error) {
        console.error("Error fetching continuity usage:", error);
      }
    };

    fetchContinuityUsage();
  }, []);

  useEffect(() => {
    const fetchMessageUsage = async () => {
      try {
        const response = await axios.get('/api/message_completion'); // Adjust the API endpoint as needed
        const complete = response.data.complete; // Users who have completed searches
        const incomplete = response.data.incomplete; // Users who have not completed searches
        const complete_count = response.data.complete_count; // Users who have completed searches
        const incomplete_count = response.data.incomplete_count; // Users who have not completed searches
        const goal = response.data.goal;
        const average_time = response.data.average_time;

        setMessageUsage({ complete, incomplete, complete_count, incomplete_count, goal, average_time });
      } catch (error) {
        console.error("Error fetching message usage:", error);
      }
    };

    fetchMessageUsage();
  }, []);

  useEffect(() => {
    // Sample data for the table
    setMilestones([
      { 
        milestone: 'Profile Completion', 
        timeframe: profileCompletionData.goal,
        completionRate: (profileCompletionData.complete * 100).toFixed(0) + '%', 
        avgTime: profileCompletionData.average_time + " days", 
        status: (profileCompletionData.average_time <= 0.8 * profileCompletionData.goal ? 'On Track' : 
          profileCompletionData.average_time <= 0.5 * profileCompletionData.goal ? 'At Risk' : 'Behind') 
      },
      { 
        milestone: 'Valuation Tool Usage', 
        timeframe: valuationToolUsage.goal,
        completionRate: (valuationToolUsage.complete).toFixed(0) + '%', 
        avgTime: valuationToolUsage.average_time + " days", 
        status: (valuationToolUsage.average_time <= 0.8 * valuationToolUsage.goal ? 'On Track' : 
                 valuationToolUsage.average_time <= 0.5 * valuationToolUsage.goal ? 'At Risk' : 'Behind') 
      },
      { 
        milestone: 'Search Usage', 
        timeframe: searchUsage.goal,
        completionRate: (searchUsage.complete).toFixed(0) + '%', 
        avgTime: searchUsage.average_time + " days", 
        status: (searchUsage.average_time <= 0.8 * searchUsage.goal ? 'On Track' : 
                 searchUsage.average_time <= 0.5 * searchUsage.goal ? 'At Risk' : 'Behind') 
      },
      { 
        milestone: 'Personal Message Usage', 
        timeframe: messageUsage.goal,
        completionRate: (messageUsage.complete).toFixed(0) + '%', 
        avgTime: messageUsage.average_time + " days", 
        status: (messageUsage.average_time <= 0.8 * messageUsage.goal ? 'On Track' : 
                 messageUsage.average_time <= 0.5 * messageUsage.goal ? 'At Risk' : 'Behind') 
      },
      { 
        milestone: 'Deal Room Usage', 
        timeframe: dealRoomUsage.goal,
        completionRate: (dealRoomUsage.complete).toFixed(0) + '%', 
        avgTime: dealRoomUsage.average_time + " days", 
        status: (dealRoomUsage.average_time <= 0.8 * dealRoomUsage.goal ? 'On Track' : 
                 dealRoomUsage.average_time <= 0.5 * dealRoomUsage.goal ? 'At Risk' : 'Behind') 
      },
      { 
        milestone: 'Continuity Usage', 
        timeframe: continuityUsage.goal,
        completionRate: (continuityUsage.complete).toFixed(0) + '%', 
        avgTime: continuityUsage.average_time, 
        status: (continuityUsage.average_time <= 0.8 * continuityUsage.goal ? 'On Track' : 
                 continuityUsage.average_time <= 0.5 * continuityUsage.goal ? 'At Risk' : 'Behind') 
      },
    ]);
  }, [profileCompletionData, valuationToolUsage, searchUsage, messageUsage, dealRoomUsage, continuityUsage]);

  // Calculate change and percentage change
  const change = activeUsers - previousActiveUsers;
  const percentageChange = previousActiveUsers !== 0 ? ((change / previousActiveUsers) * 100).toFixed(2) : 0;

  // Calculate change and percentage change for sign-ups
  const signUpsChange = signUps - previousSignUps;
  const signUpsPercentageChange = previousSignUps !== 0 ? ((signUpsChange / previousSignUps) * 100).toFixed(2) : 0;

  // Calculate change and percentage change for succession
  const successionChange = successionCount - previousSuccessionCount;
  const successionPercentageChange = previousSuccessionCount !== 0 ? ((successionChange / previousSuccessionCount) * 100).toFixed(2) : 0;

  // Calculate change and percentage change for growth
  const growthChange = growthCount - previousGrowthCount;
  const growthPercentageChange = previousGrowthCount !== 0 ? ((growthChange / previousGrowthCount) * 100).toFixed(2) : 0;

  // Calculate change and percentage change for no motivation
  const noMotivationChange = noMotivationCount - previousNoMotivationCount;
  const noMotivationPercentageChange = previousNoMotivationCount !== 0 ? ((noMotivationChange / previousNoMotivationCount) * 100).toFixed(2) : 0;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const combinedData = [
    {
      name: 'Profile Completion',
      complete: profileCompletionData.complete,
      incomplete: profileCompletionData.incomplete,
      partial: profileCompletionData.partial,
      partial_count: profileCompletionData.partial_count,
      complete_count: profileCompletionData.complete_count,
      incomplete_count: profileCompletionData.incomplete_count
    },
    {
      name: 'Valuation Tool Usage',
      complete: valuationToolUsage.complete,
      incomplete: valuationToolUsage.incomplete,
      complete_count: valuationToolUsage.complete_count,
      incomplete_count: valuationToolUsage.incomplete_count
    },
    {
      name: 'Search Usage',
      complete: searchUsage.complete,
      incomplete: searchUsage.incomplete,
      complete_count: searchUsage.complete_count,
      incomplete_count: searchUsage.incomplete_count
    },
    {
      name: 'Personal Message Usage',
      complete: messageUsage.complete,
      incomplete: messageUsage.incomplete,
      complete_count: messageUsage.complete_count,
      incomplete_count: messageUsage.incomplete_count
    },
    {
      name: 'Deal Room Usage',
      complete: dealRoomUsage.complete,
      incomplete: dealRoomUsage.incomplete,
      complete_count: dealRoomUsage.complete_count,
      incomplete_count: dealRoomUsage.incomplete_count
    },
    {
      name: 'Business Continuity Usage',
      complete: continuityUsage.complete,
      incomplete: continuityUsage.incomplete,
      complete_count: continuityUsage.complete_count,
      incomplete_count: continuityUsage.incomplete_count
    }
  ];

  console.log("Combined Data:", combinedData);

  const handleGoalChange = (index, event) => {
    const newMilestones = [...milestones];
    newMilestones[index].timeframe = event.target.value;
    setMilestones(newMilestones);
  };

  const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

  const handleSaveGoals = async () => {
    try {
      await Promise.all(milestones.map(async (milestone) => {
        await axios.post('/api/milestone_update', {
          milestone: milestone.milestone,
          goal: milestone.timeframe,
        }, {
          headers: {
            'X-CSRF-Token': csrfToken,
          }
        });
      }));
      alert('Goals updated successfully!');
    } catch (error) {
      console.error("Error saving goals:", error);
      alert('Failed to update goals.');
    }
  };

  const fetchAllData = async () => {
    if (startDate && endDate) {
      setIsLoading(true);
      
      try {
        await fetchActiveUsers();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchActiveVisitors();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchActiveUsersSuccession();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchActiveUsersGrowth();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchActiveUsersNoMotivation();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchNewMessages();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchContinuityBuilder();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchDealsCreated();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchMatches();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchInvites();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchProposals();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchBuyerNdaSent();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchSellerNdaSent();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchPipelineStages();
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCustomDateSearch = () => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      const comparisonStart = new Date(start);
      comparisonStart.setDate(comparisonStart.getDate() - diffDays - 1);
      
      const comparisonEnd = new Date(start);
      comparisonEnd.setDate(comparisonEnd.getDate() - 1);
      
      setComparisonStartDate(comparisonStart.toISOString().split('T')[0]);
      setComparisonEndDate(comparisonEnd.toISOString().split('T')[0]);
      
      fetchAllData();
    }
  };

  return (
    <div className={classes.container}>
      <h1>Success Metrics Report</h1>
      <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
        <Tab label="Usage" />
        <Tab label="User Progress" />
      </Tabs>
      {isLoading && (
        <div style={{ textAlign: 'center', margin: '20px' }}>
          <Typography variant="h5">Loading data...</Typography>
        </div>
      )}
      <div style={{ marginTop: '20px' }}>
        {tabValue === 0 && (
          <div>
            <div>
              <label style={{ fontSize: '2rem', marginRight: '10px', color: '#A9A9A9' }}>Select Date Range:</label>
              <select value={dateRange} onChange={handleDateRangeChange} style={{ fontSize: '1.5rem', marginRight: '10px' }} disabled={isLoading}>
                <option value="all_time">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_quarter">This Quarter</option>
                <option value="last_quarter">Last Quarter</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
                <option value="custom">Custom</option>
              </select>
              {dateRange === 'custom' && (
                <>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ fontSize: '1.5rem', marginRight: '10px' }}
                    disabled={isLoading}
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ fontSize: '1.5rem', marginRight: '10px' }}
                    disabled={isLoading}
                  />
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleCustomDateSearch} 
                    disabled={isLoading || !startDate || !endDate}
                    style={{ fontSize: '1.2rem', padding: '6px 16px' }}
                  >
                    Search
                  </Button>
                </>
              )}
            </div>
            {comparisonStartDate && comparisonEndDate && (
              <Typography variant="body2" style={{ fontSize: '1.2rem', color: '#A9A9A9', marginTop: '10px' }}>
                The period which these metrics will be compared against is {comparisonStartDate} to {comparisonEndDate}.
              </Typography>
            )}
            <Card variant="outlined" className={classes.card}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={2}>
                    <Typography variant="subtitle2" className={classes.subtitle}>
                      Active Users
                    </Typography>
                    <Typography variant="h5" className={classes.activeUsers}>
                      {formatNumber(activeUsers)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2" className={classes.subtitle}>
                      Active Visits
                    </Typography>
                    <Typography variant="h5" className={classes.activeUsers}>
                      {formatNumber(activeVisits)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="subtitle2" className={classes.subtitle}>
                      Active Succession
                    </Typography>
                    <Typography variant="h5" className={classes.activeUsers}>
                      {formatNumber(activeUsersSuccession)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="subtitle2" className={classes.subtitle}>
                      Active Growth
                    </Typography>
                    <Typography variant="h5" className={classes.activeUsers}>
                      {formatNumber(activeUsersGrowth)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="subtitle2" className={classes.subtitle}>
                      Active No Motivation
                    </Typography>
                    <Typography variant="h5" className={classes.activeUsers}>
                      {formatNumber(activeUsersNoMotivation)}
                    </Typography>
                  </Grid>
                </Grid>
                {previousActiveUsers !== 0 && (
                  <Typography
                    variant="body2"
                    className={classes.changeText}
                    style={{ color: change > 0 ? 'green' : 'red' }}
                  >
                    ({change > 0 ? '+' : ''}{formatNumber(change)}, {percentageChange}%)
                  </Typography>
                )}
              </CardContent>
            </Card>
            <Card variant="outlined" className={classes.card}>
              <CardContent>
                <Typography variant="subtitle2" className={classes.subtitle}>
                  Sign Ups
                </Typography>
                <Typography variant="h5" className={classes.activeUsers}>
                  {formatNumber(signUps)}
                </Typography>
                {previousSignUps !== 0 && (
                  <Typography
                    variant="body2"
                    className={classes.changeText}
                    style={{ color: signUpsChange > 0 ? 'green' : 'red' }}
                  >
                    ({signUpsChange > 0 ? '+' : ''}{formatNumber(signUpsChange)}, {signUpsPercentageChange}%)
                  </Typography>
                )}
              </CardContent>
            </Card>
            <Card variant="outlined" className={classes.card}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2" className={classes.subtitle}>
                      Succession
                    </Typography>
                    <Typography variant="h5" className={classes.activeUsers}>
                      {formatNumber(successionCount)}
                    </Typography>
                    {previousSuccessionCount !== 0 && (
                      <Typography
                        variant="body2"
                        className={classes.changeText}
                        style={{ color: successionChange > 0 ? 'green' : 'red' }}
                      >
                        ({successionChange > 0 ? '+' : ''}{formatNumber(successionChange)}, {successionPercentageChange}%)
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2" className={classes.subtitle}>
                      Growth
                    </Typography>
                    <Typography variant="h5" className={classes.activeUsers}>
                      {formatNumber(growthCount)}
                    </Typography>
                    {previousGrowthCount !== 0 && (
                      <Typography
                        variant="body2"
                        className={classes.changeText}
                        style={{ color: growthChange > 0 ? 'green' : 'red' }}
                      >
                        ({growthChange > 0 ? '+' : ''}{formatNumber(growthChange)}, {growthPercentageChange}%)
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2" className={classes.subtitle}>
                      No Motivation
                    </Typography>
                    <Typography variant="h5" className={classes.activeUsers}>
                      {formatNumber(noMotivationCount)}
                    </Typography>
                    {previousNoMotivationCount !== 0 && (
                      <Typography
                        variant="body2"
                        className={classes.changeText}
                        style={{ color: noMotivationChange > 0 ? 'green' : 'red' }}
                      >
                        ({noMotivationChange > 0 ? '+' : ''}{formatNumber(noMotivationChange)}, {noMotivationPercentageChange}%)
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            {valuationToolUsage && (
              <Card variant="outlined" className={classes.card}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        Valuation Tool Users
                      </Typography>
                      <Typography variant="h5" className={classes.activeUsers}>
                        {formatNumber(valuationToolUsage.value)}
                      </Typography>
                      {previousUsageCount !== 0 && (
                        <Typography
                          variant="body2"
                          className={classes.changeText}
                          style={{ color: valuationToolUsage.change_percentage > 0 ? 'green' : 'red' }}
                        >
                          ({valuationToolUsage.value - previousUsageCount > 0 ? '+' : ''}{formatNumber(valuationToolUsage.value - previousUsageCount)}, {valuationToolUsage.change_percentage}%)
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        Profile Views
                      </Typography>
                      <Typography variant="h5" className={classes.activeUsers}>
                        {formatNumber(profileViews)}
                      </Typography>
                      {previousProfileViews !== 0 && (
                        <Typography
                          variant="body2"
                          className={classes.changeText}
                          style={{ color: profileViews - previousProfileViews > 0 ? 'green' : 'red' }}
                        >
                          ({profileViews - previousProfileViews > 0 ? '+' : ''}{formatNumber(profileViews - previousProfileViews)}, {((profileViews - previousProfileViews) / previousProfileViews * 100).toFixed(2)}%)
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        Searches
                      </Typography>
                      <Typography variant="h5" className={classes.activeUsers}>
                        {formatNumber(searches)}
                      </Typography>
                      {previousSearches !== 0 && (
                        <Typography
                          variant="body2"
                          className={classes.changeText}
                          style={{ color: searches - previousSearches > 0 ? 'green' : 'red' }}
                        >
                          ({searches - previousSearches > 0 ? '+' : ''}{formatNumber(searches - previousSearches)}, {((searches - previousSearches) / previousSearches * 100).toFixed(2)}%)
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        Accessed Education
                      </Typography>
                      <Typography variant="h5" className={classes.activeUsers}>
                        {formatNumber(accessedEducation)}
                      </Typography>
                      {previousAccessedEducation !== 0 && (
                        <Typography
                          variant="body2"
                          className={classes.changeText}
                          style={{ color: accessedEducation - previousAccessedEducation > 0 ? 'green' : 'red' }}
                        >
                          ({accessedEducation - previousAccessedEducation > 0 ? '+' : ''}{formatNumber(accessedEducation - previousAccessedEducation)}, {((accessedEducation - previousAccessedEducation) / previousAccessedEducation * 100).toFixed(2)}%)
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        New Conversations
                      </Typography>
                      <Typography variant="h5" className={classes.activeUsers}>
                        {formatNumber(newConversations)}
                      </Typography>
                      {previousNewConversations !== 0 && (
                        <Typography
                          variant="body2"
                          className={classes.changeText}
                          style={{ color: newConversations - previousNewConversations > 0 ? 'green' : 'red' }}
                        >
                          ({newConversations - previousNewConversations > 0 ? '+' : ''}{formatNumber(newConversations - previousNewConversations)}, {((newConversations - previousNewConversations) / previousNewConversations * 100).toFixed(2)}%)
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        New Messages
                      </Typography>
                      <Typography variant="h5" className={classes.activeUsers}>
                        {formatNumber(newMessages)}
                      </Typography>
                      {previousNewMessages !== 0 && (
                        <Typography
                          variant="body2"
                          className={classes.changeText}
                          style={{ color: newMessages - previousNewMessages > 0 ? 'green' : 'red' }}
                        >
                          ({newMessages - previousNewMessages > 0 ? '+' : ''}{formatNumber(newMessages - previousNewMessages)}, {((newMessages - previousNewMessages) / previousNewMessages * 100).toFixed(2)}%)
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={3}>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        Continuity Builder
                      </Typography>
                      <Typography variant="h5" className={classes.activeUsers}>
                        {formatNumber(continuityBuilderCount)}
                      </Typography>
                      {previousContinuityBuilderCount !== 0 && (
                        <Typography
                          variant="body2"
                          className={classes.changeText}
                          style={{ color: continuityBuilderCount - previousContinuityBuilderCount > 0 ? 'green' : 'red' }}
                        >
                          ({continuityBuilderCount - previousContinuityBuilderCount > 0 ? '+' : ''}{formatNumber(continuityBuilderCount - previousContinuityBuilderCount)}, {((continuityBuilderCount - previousContinuityBuilderCount) / previousContinuityBuilderCount * 100).toFixed(2)}%)
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
            <Card variant="outlined" className={classes.card}>
              <CardContent>
                <Typography variant="h5" className={classes.subtitle}>
                  Deal Tracking Pipeline Stages
                </Typography>
                <Grid container spacing={2}>
                  {pipelineStages.map((stage) => (
                    <Grid item xs={4} key={stage.name}>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        {stage.name} {/* Stage Name */}
                      </Typography>
                      <Typography variant="h5" className={classes.activeUsers}>
                        {formatNumber(stage.count)} {/* Count of stages */}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </div>
        )}
        {tabValue === 1 && (
          <Card variant="outlined" className={classes.card}>
            <CardContent>
              <ClickableGoalsProgressChart 
                combinedData={combinedData} 
                classes={classes} 
              />
              {/* Table for Milestones */}
              <TableContainer component={Paper} style={{ marginTop: '20px' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Milestone</strong></TableCell>
                      <TableCell><strong>Timeframe (Days)</strong></TableCell>
                      <TableCell><strong>Completion Rate</strong></TableCell>
                      <TableCell><strong>Avg Time</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {milestones.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.milestone}</TableCell>
                        <TableCell>
                          <TextField
                            value={row.timeframe}
                            onChange={(event) => handleGoalChange(index, event)}
                            type="number"
                            inputProps={{ min: "0" }}
                          />
                        </TableCell>
                        <TableCell>{row.completionRate}</TableCell>
                        <TableCell>{row.avgTime}</TableCell>
                        <TableCell>
                          <Chip 
                            label={row.status} 
                            style={{
                              backgroundColor: row.status === 'On Track' ? '#639754' : 
                                               row.status === 'At Risk' ? '#FFD301' : 
                                               '#E03C32',
                              color: 'white'
                            }} 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button variant="contained" color="primary" onClick={handleSaveGoals} style={{ marginTop: '20px' }}>
                Save Goals
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default withStyles(styles)(SuccessMetricsContainer); 